import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Collapse } from 'react-bootstrap';
import { FiFilter, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { END_OF_STAY_SCOPE_OPTIONS } from '../utils/dashboardConstants';
import { getAllPartners } from '../../../../services/api';

const EndOfStayFilters = ({
    filters,
    onFilterChange,
    onReset,
    hasActiveFilters,
    showPartnerFilter = true,
}) => {
    const [localPolicyCode, setLocalPolicyCode] = useState(filters.policy_code);
    const [localCompanyName, setLocalCompanyName] = useState(filters.company_name);
    const [partners, setPartners] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(true);

    useEffect(() => {
        setLocalPolicyCode(filters.policy_code);
    }, [filters.policy_code]);

    useEffect(() => {
        setLocalCompanyName(filters.company_name);
    }, [filters.company_name]);

    useEffect(() => {
        if (!showPartnerFilter) return undefined;

        let cancelled = false;

        const loadPartners = async () => {
            try {
                const response = await getAllPartners();
                const list = response?.partners || response?.data || response || [];
                if (!cancelled) {
                    setPartners(Array.isArray(list) ? list : []);
                }
            } catch (error) {
                console.error('Failed to load partners:', error);
            }
        };

        loadPartners();
        return () => { cancelled = true; };
    }, [showPartnerFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localPolicyCode !== filters.policy_code) {
                onFilterChange('policy_code', localPolicyCode);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [localPolicyCode, filters.policy_code, onFilterChange]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localCompanyName !== filters.company_name) {
                onFilterChange('company_name', localCompanyName);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [localCompanyName, filters.company_name, onFilterChange]);

    return (
        <Card className="mb-3 admin-ds-filters-card">
            <Card.Header className="admin-ds-filters-card__header d-flex justify-content-between align-items-center">
                <h6 className="mb-0 admin-ds-filters-card__title d-flex align-items-center gap-2">
                    <FiFilter size={14} className="d-md-none" />
                    Filtres de recherche
                </h6>
                <div className="d-flex gap-2 admin-ds-filters-card__actions">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="d-md-none"
                        onClick={() => setFiltersOpen((prev) => !prev)}
                        aria-expanded={filtersOpen}
                    >
                        {filtersOpen ? <FiChevronUp /> : <FiChevronDown />}
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="outline-secondary" size="sm" onClick={onReset}>
                            Réinitialiser
                        </Button>
                    )}
                </div>
            </Card.Header>
            <Collapse in={filtersOpen}>
                <div>
                    <Card.Body className="admin-ds-filters-card__body">
                        <Row className="g-3 admin-ds-filters-row">
                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group className="mb-0">
                                    <Form.Label>Scope</Form.Label>
                                    <Form.Select
                                        value={filters.scope}
                                        onChange={(e) => onFilterChange('scope', e.target.value)}
                                    >
                                        {END_OF_STAY_SCOPE_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group className="mb-0">
                                    <Form.Label>Fin séjour du</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.trip_end_date_from || ''}
                                        onChange={(e) => onFilterChange('trip_end_date_from', e.target.value || null)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group className="mb-0">
                                    <Form.Label>Fin séjour au</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.trip_end_date_to || ''}
                                        onChange={(e) => onFilterChange('trip_end_date_to', e.target.value || null)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group className="mb-0">
                                    <Form.Label>Code police</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={localPolicyCode}
                                        onChange={(e) => setLocalPolicyCode(e.target.value)}
                                        placeholder="Rechercher…"
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group className="mb-0">
                                    <Form.Label>Partenaire (nom)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={localCompanyName}
                                        onChange={(e) => setLocalCompanyName(e.target.value)}
                                        placeholder="Recherche partielle…"
                                    />
                                </Form.Group>
                            </Col>

                            {showPartnerFilter && (
                                <Col xs={12} sm={6} md={4} lg={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Partenaire (ID)</Form.Label>
                                        <Form.Select
                                            value={filters.partner_id}
                                            onChange={(e) => onFilterChange('partner_id', e.target.value)}
                                        >
                                            <option value="">Tous</option>
                                            {partners.map((partner) => {
                                                const id = partner.partner_id || partner.id;
                                                const name = partner.company_name || partner.name || id;
                                                return (
                                                    <option key={id} value={id}>
                                                        {name}
                                                    </option>
                                                );
                                            })}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
};

export default EndOfStayFilters;
